import React, { useState, useEffect, useMemo, memo, useCallback } from 'react'
import { Button, TextField, CircularProgress } from '@material-ui/core'
import filter from 'assets/icon/filter.svg'
import { Icon, Step } from 'semantic-ui-react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import Add from 'assets/icon/Add.svg'
import { useDispatch, useSelector } from "react-redux"
import { modalToggle } from 'redux/actions/general'
import { getContact, getEmployee } from 'redux/actions/master'
import { getPipeline, setCards, updateStage, setProposal, getDetailDeal } from 'redux/actions/pipeline'
import Chevron from 'assets/icon/chevron-right.svg'
import { MuiThemeProvider, createMuiTheme, makeStyles } from '@material-ui/core/styles'
import Checklist from 'assets/icon/checklist.png'
import close from 'assets/icon/close.svg'
import * as actionType from 'redux/constants/pipeline'
import { handle_access } from 'service/handle_access'
import moment from 'moment'

const themeButton = createMuiTheme({ 
    palette: { 
        primary: { main:'#70bf4e', contrastText: '#FFFFFF' },
        secondary: { main:'#ff7165', contrastText: '#FFFFFF' }
    } 
})

const useStyles = makeStyles(() => ({
    textField: {
      [`& fieldset`]: { borderRadius: 10 },
      width: '100%',
      marginBottom: 15
    }
}));

// Helper Formatter ringan (Pengganti NumberFormat agar tidak merender React Component berlebih)
const currencyFormatter = new Intl.NumberFormat('id-ID');
const formatCurrency = (val) => currencyFormatter.format(val || 0);

const getProbability = (key) => (key >= 10 && key <= 100) ? key / 100 : 0;

const renderTextStageColor = (prob) => {
    if (prob <= 30) return '#ff6e79';
    if (prob <= 60) return '#ffb100';
    return '#70bf4e';
}

const renderTextAgeColor = (age) => {
    if (age <= 30) return '#4e8637';
    if (age <= 90) return '#FFB100';
    return '#ff6e79';
}

// -------------------------------------------------------------
// 1. DEALS COMPONENT (Memoized & Optimized)
// -------------------------------------------------------------
const Deals = memo(({
    deal, index, card_id, token, profile, loading, updated, dragSuccess,
    modalsToggle, modal_action, backdropToggle, tabsToggle
}) => {
    const dispatch = useDispatch()
    const employeeCount = useSelector(state => state.master.employee.length)

    const handleFetchEmployeeIfNeeded = useCallback(async () => {
        if (employeeCount < 1) {
            await dispatch(getEmployee(token))
        }
    }, [dispatch, employeeCount, token]);

    const addSalesVisit = useCallback(async (clientId, dealId) => {
        let res = await dispatch(getContact(token, clientId))
        if (res) {
            await handleFetchEmployeeIfNeeded()
            dispatch(modalToggle({
                modal_open: true,
                modal_title: `Sales Visit to ${res.data.client.company}`,
                modal_component: "sales_visit",
                modal_data: { clientId, dealId },
                modal_size: 500,
                modal_action: 'add_sales_visit'
            }))
        }
    }, [dispatch, token, handleFetchEmployeeIfNeeded])

    const detailDeal = useCallback(async (clientId, dealId) => {
        let res = await dispatch(getDetailDeal(token, dealId))
        if (res) {
            await handleFetchEmployeeIfNeeded()
            tabsToggle('detail', 'pipeline')
        }
    }, [dispatch, token, handleFetchEmployeeIfNeeded, tabsToggle])

    const addProposal = useCallback(async (clientId, dealId) => {
        dispatch(setProposal({ dealId }))
        let res = await dispatch(getContact(token, clientId))
        if (res) {
            await handleFetchEmployeeIfNeeded()
            dispatch({ type: actionType.RESET_PROPOSAL })
            dispatch({ type: actionType.SET_CLIENT_ID, payload: clientId })
            dispatch(modalToggle({
                modal_open: true,
                modal_title: `Upload Proposal for ${res.data.client.company}`,
                modal_component: "proposal",
                modal_data: { clientId, dealId },
                modal_size: 550,
                modal_action: 'add_proposal',
                modal_type: 'multi'
            }))
        }
    }, [dispatch, token, handleFetchEmployeeIfNeeded])

    const wonDeal = useCallback((dealId, invoicePeriod) => {
        if (invoicePeriod === 0) {
            dispatch(modalToggle({ 
                modal_open: true, modal_title: "Must include Invoice Period",
                modal_component: "on_authorize", modal_size: 300, modal_type: 'alert',
                modal_data: { msg: "<p>Deal cant be won if invoice period still empty</p>" },
                modal_action: 'on_authorize'
            }))
        } else {
            dispatch(modalToggle({ 
                modal_open: true, modal_title: `Yeaah!! did we win it?`,
                modal_component: "confirm2", modal_size: 400, modal_type: 'confirm',
                modal_data: {
                    dealId, title: `Yeaah!! did we Won it?`,
                    msg: `<p>Congratulations on your achievement this time, thank you for giving your best.</p>`,
                    title_cancel: 'No, Cancel', title_yes: 'Yes, Won it'
                },
                modal_action: 'won_deal'
            }))
        }
    }, [dispatch])

    const deleteDeal = useCallback((dealId) => {
        backdropToggle()
        dispatch(modalToggle({ 
            modal_open: true, modal_title: `Are you sure delete this deal?`,
            modal_component: "confirm2", modal_size: 400, modal_type: 'confirm',
            modal_data: {
                dealId, msg: `<p>This action will make deal delete permanent from our system</p>`,
                title_cancel: 'No, Cancel', title_yes: 'Yes, Delete deal'
            },
            modal_action: 'delete_deal'
        }))
    }, [dispatch, backdropToggle])

    const lostDeal = useCallback((dealId) => {
        dispatch(modalToggle({ 
            modal_open: true, modal_title: `Are you sure this deal is losing?`,
            modal_component: "confirm2", modal_size: 400, modal_type: 'confirm',
            modal_data: {
                dealId, msg: `<p>never mind, keep your spirits up. This offer will be on the <b>Lost Deal</b> list if at any time there are changes</p>`,
                title_cancel: 'No, Cancel', title_yes: 'Yes, lost it'
            },
            modal_action: 'lost_deal'
        }))
    }, [dispatch])

    // Pre-calculate formatted values
    const isValueLarge = deal.proposalValue.toString().length >= 8;
    const formattedShortValue = useMemo(() => {
        return isValueLarge ? `${Math.round(deal.proposalValue / 1000000)}` : deal.proposalValue;
    }, [deal.proposalValue, isValueLarge]);

    const canAccess = useMemo(() => {
        return handle_access(profile.id, profile.roleId, deal.access, deal.rms, deal.title);
    }, [profile.id, profile.roleId, deal.access, deal.rms, deal.title]);

    const isDealLoading = loading[deal.id];
    const isDealUpdated = updated[deal.id];
    const isModalOpen = modal_action[deal.id];

    return (
        <Draggable draggableId={deal.id} index={index}>
            {(provided) => (
                <div className='deals-card' {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
                    {isDealLoading ? (
                        <div style={{width:180, height:100, display:'flex', justifyContent:'center', alignItems:'center'}}>
                            <CircularProgress color="secondary" size={30}/>
                        </div>
                    ) : isDealUpdated ? (
                        <div className='success-drag'>
                            <img src={dragSuccess ? Checklist : close} style={{width:60}} alt={dragSuccess ? "success" : "failed"}/>
                        </div>
                    ) : (
                        <div className='deals-card-content'>
                            <p className='deals-card-title' onClick={() => detailDeal(deal.clientId, deal.id)}>
                                {deal.title.length > 60 ? `${deal.title.substring(0,60)}...` : deal.title}
                                {deal.title.length > 60 && <span className='tooltip-text'>{deal.title}</span>}
                            </p>
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                <p className='deals-card-company' style={{maxWidth:140}}>{deal.clientName}</p>
                                {canAccess && (
                                    <div className='circle-chevron' onClick={() => modalsToggle(deal.id)}>
                                        <img src={Chevron} style={{width:7}} alt="chevron"/><br/>
                                    </div>
                                )}
                            </div>
                            <div className='deals-card-footer'>
                                <div className='proposal-value'>
                                    <p style={{color:'#4e8637', cursor:'pointer', fontWeight:'bold', fontSize:12}}>
                                        IDR {formatCurrency(formattedShortValue)} {isValueLarge && 'M'}
                                    </p>
                                    <span className='tooltip-text-proposal-value'>
                                        <div className='div-flex' style={{justifyContent:'center'}}>
                                            IDR&nbsp;{formatCurrency(deal.proposalValue)}
                                        </div>
                                    </span>
                                </div>
                                <p style={{color: renderTextAgeColor(deal.age), fontSize:10}}>
                                    {deal.age < 0 ? 0 : deal.age} day &nbsp;&nbsp;
                                    <span style={{color: renderTextStageColor(deal.probability)}}>{getProbability(deal.probability)}</span>
                                </p>
                            </div>
                            <div className='deals-card-footer' style={{marginTop: 10}}>
                                <div className='proposal-value'>
                                    <p style={{color:'#4e8637', cursor:'pointer', fontWeight:'bold', fontSize:12}}>
                                        Status Update: {deal.statusDate === null ? '-' : moment(deal.statusDate).format('DD-MM-YYYY')} 
                                    </p>
                                    <span className='tooltip-text-proposal-value'>
                                        <div className='div-flex' style={{justifyContent:'center'}}>Status</div>
                                    </span>
                                </div>
                            </div>
                        </div>
                    )} 
                    
                    <div className={`modal-deals${card_id === 'negotiations' ? '-left' : ''}`} style={{display: isModalOpen ? 'block' : 'none'}}>
                        <div className='modal-deals-item' onClick={() => addSalesVisit(deal.clientId, deal.id)}>
                            <img src={Add} style={{width:20}} alt="add"/>&nbsp;&nbsp;
                            <h3>Add Sales Visit</h3>
                        </div>
                        {card_id !== 'lead-in' && (
                            <div className='modal-deals-item' onClick={() => addProposal(deal.clientId, deal.id)}>
                                <img src={Add} style={{width:20}} alt="add"/>&nbsp;&nbsp;
                                <h3>Upload Proposal</h3>
                            </div>
                        )}
                        <div className='modal-deals-footer'>
                            <MuiThemeProvider theme={themeButton}>
                                <Button onClick={() => wonDeal(deal.id, deal.invoicePeriod)} size='small' color='primary' variant='contained' className='btn-remove-capital btn-rounded remove-boxshadow' style={{width:80}}>Won</Button>&nbsp;&nbsp;
                                <Button onClick={() => lostDeal(deal.id)} size='small' color='secondary' variant='contained' className='btn-remove-capital btn-rounded remove-boxshadow' style={{width:80}}>Lost</Button>&nbsp;&nbsp;
                                <Button onClick={() => deleteDeal(deal.id)} size='small' color='secondary' variant='text' className='btn-remove-capital btn-rounded remove-boxshadow' style={{width:80, backgroundColor:'#eeeeee'}}>Delete</Button>
                            </MuiThemeProvider>
                        </div>
                    </div>
                </div>
            )}
        </Draggable>
    )
})

// -------------------------------------------------------------
// 2. CARD / COLUMN COMPONENT (Optimized)
// -------------------------------------------------------------
const Card = memo(({
    cardOrderLength, card, allDeals, tabsToggle, index, token, profile, 
    loading, updated, dragSuccess, modalsToggle, modal_action, backdropToggle
}) => {
    const dispatch = useDispatch()

    const addDeal = useCallback(() => {
        dispatch(modalToggle({
            modal_open: true,
            modal_title: "Add new deal",
            modal_component: "add_deal",
            modal_data: card.card_probability,
            modal_size: 500,
            modal_action: 'add_deal'
        }))
    }, [dispatch, card.card_probability]);

    return (
        <div className='pipeline-container'>
            <div className={index === cardOrderLength - 1 ? 'pipeline-step-last' : 'pipeline-step'} style={{zIndex: 5 - index, justifyContent: card.id === 'proposal-development' ? 'flex-end' : 'center'}}>
                <p>{card.title}</p>
            </div>
            <Droppable droppableId={card.id}>
                {(provided) => (
                    <div className='pipeline-deals' {...provided.droppableProps} ref={provided.innerRef}>
                        {card.dealsId.map((dealId, idx) => {
                            const deal = allDeals[dealId];
                            if (!deal) return null;
                            return (
                                <Deals 
                                    key={dealId}
                                    deal={deal}
                                    index={idx}
                                    card_id={card.id}
                                    backdropToggle={backdropToggle} 
                                    modalsToggle={modalsToggle} 
                                    modal_action={modal_action} 
                                    dragSuccess={dragSuccess} 
                                    updated={updated} 
                                    loading={loading} 
                                    token={token} 
                                    profile={profile} 
                                    tabsToggle={tabsToggle}
                                />
                            )
                        })}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
            <Button onClick={addDeal} style={{textTransform:'none', width:180, boxShadow:'none', marginLeft:18}} color='secondary' variant="contained">
                <img src={Add} style={{width:20}} alt="add"/>
            </Button>
        </div>
    )
})

// -------------------------------------------------------------
// 3. MAIN PIPELINE COMPONENT
// -------------------------------------------------------------
export default function Pipeline(props) {
    // Select specific Redux slices to prevent unwanted global re-renders
    const pipeline = useSelector(state => state.pipeline)
    const masterTribes = useSelector(state => state.master.tribes)
    const masterSegments = useSelector(state => state.master.segments)
    const dispatch = useDispatch()
    const classes = useStyles()

    const [modal, setModal] = useState(false)
    const [modal_action, openModal] = useState({})
    const [backdrop, openBackdrop] = useState(false)
    const [loading, setLoading] = useState({})
    const [dragSuccess, setDragSuccess] = useState({})
    const [updated, setUpdated] = useState({})
    const [prob, setProb] = useState('')
    const [total_after, setTotalAfter] = useState(null)

    const { tribe, segment, rm, probability, textPeriode, periode } = pipeline.filter

    useEffect(() => {
        const map = probability.map((data) => `${data.id}`)
        dispatch(
            getPipeline(
                props.token,
                `${periode.fromMonth}/${periode.toMonth}/${tribe}/${segment}/${rm.value}/${probability.length > 0 ? map : 0}`
            )
        )
        setTotalAfter(null)
    }, [])

    const countToggle = useCallback(() => {
        openBackdrop(prev => !prev)
        setModal(prev => !prev)
    }, [])

    const backdropToggle = useCallback(() => {
        openBackdrop(prev => !prev)
        openModal({})
        setModal(false)
    }, [])

    const modalsToggle = useCallback((dealId) => {
        openBackdrop(prev => !prev)
        openModal(prev => ({ ...prev, [dealId]: !prev[dealId] }))
    }, [])

    const onDragEnd = useCallback(async (result) => {
        const { draggableId, source, destination } = result;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const start = pipeline.cards[source.droppableId];
        const finish = pipeline.cards[destination.droppableId];
        const targetDeal = pipeline.deals[draggableId];

        if (!targetDeal) return;

        if (handle_access(props.profile.id, props.profile.roleId, targetDeal.access, targetDeal.rms)) {
            if (start === finish) {
                const newDealsId = Array.from(start.dealsId)
                newDealsId.splice(source.index, 1)
                newDealsId.splice(destination.index, 0, draggableId)
                const newCard = { ...start, dealsId: newDealsId }
                dispatch(setCards({ ...pipeline.cards, [newCard.id]: newCard }))
                return
            }

            const startDealId = Array.from(start.dealsId);
            startDealId.splice(source.index, 1);
            
            const finishDealId = Array.from(finish.dealsId);
            finishDealId.splice(destination.index, 0, draggableId);

            dispatch(setCards({
                ...pipeline.cards,
                [start.id]: { ...start, dealsId: startDealId },
                [finish.id]: { ...finish, dealsId: finishDealId }
            }))

            setLoading({ [draggableId]: true })

            let res = await dispatch(updateStage(props.token, `/${draggableId}/${props.profile.id}/${finish.stage_id}`))
            
            setLoading({ [draggableId]: false })
            setUpdated({ [draggableId]: true })
            setDragSuccess({ [draggableId]: !!res })
            
            setTimeout(() => setUpdated({ [draggableId]: false }), 2000)
        } else {
            dispatch(modalToggle({ 
                modal_open: true, modal_title: "Hold Up!", modal_component: "on_authorize",
                modal_size: 250, modal_type: 'alert',
                modal_data: { msg: "<p>Authorized owner's only</p>" },
                modal_action: 'on_authorize'
            }))
        }
    }, [dispatch, pipeline.cards, pipeline.deals, props.profile, props.token])

    const addFilter = useCallback(() => {
        dispatch(modalToggle({
            modal_open: true, modal_title: "Pipeline Filter",
            modal_component: "pipeline_filter", modal_data: null,
            modal_size: 300, modal_action: 'add_pipeline_filter',
        }))
    }, [dispatch])

    const tabsToggle = useCallback((key, back) => {
        props.tabToggle(key, back)
    }, [props.tabToggle])

    const addDeal = useCallback(() => {
        dispatch(modalToggle({
            modal_open: true, modal_title: "Add new deal",
            modal_component: "add_deal", modal_data: null,
            modal_size: 500, modal_action: 'add_deal'
        }))
    }, [dispatch])

    const countProb = useCallback(() => {
        countToggle()
        setTotalAfter(prob * pipeline.total_pv)
    }, [countToggle, prob, pipeline.total_pv])

    // Memoize filter labels to avoid re-filtering every render
    const tribeText = useMemo(() => {
        if (tribe === 0) return 'All Tribe';
        return masterTribes.find(data => data.id === tribe)?.text || '';
    }, [tribe, masterTribes]);

    const segmentText = useMemo(() => {
        if (segment === 0) return 'All Segment';
        return masterSegments.find(data => data.id === segment)?.text || '';
    }, [segment, masterSegments]);

    const formattedTotalPV = useMemo(() => {
        const total = pipeline.total_pv || 0;
        const isLarge = total.toString().length >= 7;
        const val = isLarge ? Math.round(total / 1000000) : total;
        return { text: formatCurrency(val), isLarge };
    }, [pipeline.total_pv]);

    const formattedTotalAfter = useMemo(() => {
        if (total_after === null) return null;
        const isLarge = total_after.toString().length >= 7;
        const val = isLarge ? Math.round(total_after / 1000000) : total_after;
        return { text: formatCurrency(val), isLarge };
    }, [total_after]);

    const canAddDeal = useMemo(() => {
        return handle_access(props.profile.id, props.profile.roleId, [], [{ id: props.profile.id }]);
    }, [props.profile.id, props.profile.roleId]);

    return (
        <div>
            <div className='head-section'>
                <div>
                    {canAddDeal && (
                        <>
                            <Button onClick={addDeal} size='small' color='primary' variant='contained' className='btn-remove-capital btn-rounded remove-boxshadow' style={{width:100}}>Add deal</Button>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                        </>
                    )}
                    <Button onClick={() => tabsToggle('projection')} size='small' variant='contained' className='btn-remove-capital btn-rounded remove-boxshadow' style={{color:'white', backgroundColor:'#ffb100', width:130}}>Projection deal</Button>
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <Button onClick={() => tabsToggle('summary')} size='small' variant='contained' className='btn-remove-capital btn-rounded remove-boxshadow' style={{color:'white', backgroundColor:'#ffb100', width:180}}>Projection summary</Button>
                </div>
                <div style={{display:'flex', alignItems:'center'}}>
                    <div>
                        <p className='pipeline-filterby'><b>Filter by:</b></p>
                        <div style={{display:'flex', flexWrap:'wrap', width:400}}>
                            <p className='pipeline-filterby'>Tribe : {tribeText}&nbsp;&nbsp;&nbsp;&nbsp;</p>
                            <p className='pipeline-filterby'>Deal period : {textPeriode}&nbsp;&nbsp;&nbsp;&nbsp;</p>
                            <p className='pipeline-filterby'>Segment : {segmentText}&nbsp;&nbsp;&nbsp;&nbsp;</p>
                            <p className='pipeline-filterby'>RM : {rm !== null ? rm.label : 'All RM'}&nbsp;&nbsp;&nbsp;&nbsp;</p>
                            <p className='pipeline-filterby'>
                                {probability.length > 0 && "Probability: " + probability.map((data) => `${getProbability(data.id)}`)} &nbsp;&nbsp;&nbsp;&nbsp;
                            </p>
                        </div>
                    </div>
                    <button onClick={addFilter} className='card-table__head_btn'>
                        <img src={filter} style={{width:20}} alt="filter"/>&nbsp;&nbsp;Filter
                    </button>
                </div>
            </div>
            <br/>
            <div className='pipeline-wrapper'>
                <DragDropContext onDragEnd={onDragEnd}>
                    {pipeline.cardOrder.map((cardId, index) => {
                        const card = pipeline.cards[cardId];
                        if (!card) return null;

                        return (
                            <Card
                                key={cardId}
                                cardOrderLength={pipeline.cardOrder.length}
                                modalsToggle={modalsToggle}
                                modal_action={modal_action}
                                dragSuccess={dragSuccess}
                                updated={updated}
                                loading={loading}
                                token={props.token}
                                profile={props.profile}
                                tabsToggle={tabsToggle}
                                card={card}
                                allDeals={pipeline.deals}
                                index={index}
                                backdropToggle={backdropToggle}
                            />
                        )
                    })}
                </DragDropContext>
            </div>
            <div className='pipeline-footer'>
                <div onClick={backdropToggle} style={{zIndex:2, width:'100%', height:700, position:'absolute', left:0, display: backdrop ? 'block' : 'none'}}></div>
                <p>Total proposal value: IDR {formattedTotalPV.text} {formattedTotalPV.isLarge && 'M'}</p>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <p>
                    Total after Probability: IDR {formattedTotalAfter && `${formattedTotalAfter.text} ${formattedTotalAfter.isLarge ? 'M' : ''}`}
                    &nbsp;&nbsp;
                    <Button onClick={countToggle} size='small' color='primary' variant='outlined' className='btn-rounded' style={{width:90, height:30, backgroundColor:'white'}}>Count it</Button>
                </p>
                <div className='modal-count-probability' style={{display: modal ? 'flex' : 'none'}}>
                    <div style={{width:'60%'}}>
                        <TextField
                            label='Probability'
                            type='text'
                            value={prob}
                            onChange={(e) => setProb(e.target.value)}
                            variant='outlined'
                            size='small'
                            className={classes.textField}
                            style={{margin:0}}
                        />
                    </div>
                    <Button onClick={countProb} size='small' color='primary' variant='contained' className='btn-remove-capital btn-rounded remove-boxshadow' style={{width:120, height:30}}>Count it</Button>
                </div>
            </div>
        </div>
    )
}